import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ExternalLink,
  Pencil,
  Trash2,
  Target,
  Waypoints,
  Server,
  ListChecks,
  GitBranch,
  Scale,
  ArrowUpRight,
  AlertTriangle,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  Spinner,
  useToast,
} from '../../ui';
import { useDocuments } from '../../features/documents/useDocuments';
import { docTypeLabel } from '../../features/documents/documentConfig';
import {
  formatDate,
  isStale,
  stalenessDays,
  statusLabel,
  statusTone,
} from '../../features/documents/documentModel';
import type { TeamDocument } from '../../features/documents/types';
import { DocumentModal } from './DocumentModal';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="mb-2 text-sm font-semibold text-zinc-100">{title}</h2>
      {children}
    </div>
  );
}

/** Document detail — full metadata, relationships, supersede chain, and edit. */
export function DocumentDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    documents,
    goals,
    workstreams,
    tasks,
    services,
    decisions,
    currentAlias,
    goalTitles,
    workstreamTitles,
    storyTitles,
    serviceTitles,
    decisionTitles,
    documentTitles,
    loading,
    error,
    get,
    update,
    remove,
    structureFromNotes,
  } = useDocuments();

  const doc = useMemo(() => documents.find((d) => d.id === id), [documents, id]);
  const [editing, setEditing] = useState<TeamDocument | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const openEdit = async () => {
    const full = await get(id);
    if (!full) {
      toast({ tone: 'error', title: 'Not found', description: 'Document could not be loaded.' });
      return;
    }
    setEditing(full);
    setModalOpen(true);
  };

  const onSubmit = async (draft: Parameters<typeof update>[1]) => {
    await update(id, draft);
    toast({ tone: 'success', title: 'Document updated', description: draft.title });
  };

  const doDelete = async () => {
    if (!doc) return;
    setDeleting(true);
    try {
      await remove(doc.id);
      toast({ tone: 'success', title: 'Document removed', description: doc.title });
      navigate('/documents');
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Could not remove document',
        description: e instanceof Error ? e.message : 'Delete failed.',
      });
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner label="Loading document" />
      </div>
    );
  }
  if (error) {
    return <EmptyState icon={<AlertTriangle className="h-6 w-6" />} title="Couldn't load document" description={error} />;
  }
  if (!doc) {
    return (
      <EmptyState
        icon={<AlertTriangle className="h-6 w-6" />}
        title="Document not found"
        description="It may have been removed."
        action={<Button onClick={() => navigate('/documents')}>Back to Document Bank</Button>}
      />
    );
  }

  const stale = isStale(doc);
  const days = stalenessDays(doc);
  const supersedesTitle = doc.supersedesId ? documentTitles.get(doc.supersedesId) : undefined;
  const supersededByTitle = doc.supersededById ? documentTitles.get(doc.supersededById) : undefined;

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate('/documents')}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-zinc-400 transition-colors hover:text-zinc-200"
      >
        <ArrowLeft className="h-4 w-4" /> Document Bank
      </button>

      <PageHeader
        title={doc.title || 'Untitled document'}
        description={doc.summary}
        actions={
          <>
            {doc.documentLink && (
              <a href={doc.documentLink} target="_blank" rel="noreferrer">
                <Button variant="secondary" leftIcon={<ExternalLink className="h-4 w-4" />}>
                  Open
                </Button>
              </a>
            )}
            <Button variant="outline" leftIcon={<Pencil className="h-4 w-4" />} onClick={openEdit}>
              Edit
            </Button>
            <Button variant="ghost" leftIcon={<Trash2 className="h-4 w-4" />} onClick={() => setConfirmingDelete(true)}>
              Delete
            </Button>
          </>
        }
      />

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Badge tone="neutral">{docTypeLabel(doc.docType)}</Badge>
        <Badge tone={statusTone(doc.status)} dot>
          {statusLabel(doc.status)}
        </Badge>
        {stale && <Badge tone="warning">stale{days !== undefined ? ` · ${days}d` : ''}</Badge>}
        {doc.createdDate && <span className="text-xs text-zinc-500">created {formatDate(doc.createdDate)}</span>}
        {doc.lastReviewedDate && (
          <span className="text-xs text-zinc-500">reviewed {formatDate(doc.lastReviewedDate)}</span>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {doc.contextWhy && (
            <Card>
              <CardBody>
                <Section title="Why it exists">
                  <p className="whitespace-pre-wrap text-sm text-zinc-300">{doc.contextWhy}</p>
                </Section>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardBody className="space-y-4">
              <Section title="Relationships">
                <div className="flex flex-wrap items-center gap-1.5">
                  {doc.relatedGoalId && (
                    <button type="button" onClick={() => navigate(`/goals/${encodeURIComponent(doc.relatedGoalId!)}`)}>
                      <Badge tone="accent"><Target className="h-3 w-3" /> {goalTitles.get(doc.relatedGoalId) ?? 'Goal'}</Badge>
                    </button>
                  )}
                  {doc.relatedWorkstreamId && (
                    <button type="button" onClick={() => navigate(`/workstreams/${encodeURIComponent(doc.relatedWorkstreamId!)}`)}>
                      <Badge tone="info"><Waypoints className="h-3 w-3" /> {workstreamTitles.get(doc.relatedWorkstreamId) ?? 'Workstream'}</Badge>
                    </button>
                  )}
                  {doc.relatedServices.map((s) => (
                    <button key={s} type="button" onClick={() => navigate(`/services/${encodeURIComponent(s)}`)}>
                      <Badge tone="neutral"><Server className="h-3 w-3" /> {serviceTitles.get(s) ?? s}</Badge>
                    </button>
                  ))}
                  {doc.relatedStoryIds.map((s) => (
                    <Badge key={s} tone="neutral"><ListChecks className="h-3 w-3" /> {storyTitles.get(s) ?? s}</Badge>
                  ))}
                  {doc.relatedDecisionIds.map((s) => (
                    <button key={s} type="button" onClick={() => navigate(`/decisions/${encodeURIComponent(s)}`)}>
                      <Badge tone="accent"><Scale className="h-3 w-3" /> {decisionTitles.get(s) ?? s}</Badge>
                    </button>
                  ))}
                  {doc.relatedRepo && (
                    <Badge tone="neutral"><GitBranch className="h-3 w-3" /> {doc.relatedRepo}</Badge>
                  )}
                  {!doc.relatedGoalId &&
                    !doc.relatedWorkstreamId &&
                    doc.relatedServices.length === 0 &&
                    doc.relatedStoryIds.length === 0 &&
                    doc.relatedDecisionIds.length === 0 &&
                    !doc.relatedRepo && (
                      <span className="text-sm text-zinc-500">No linked entities.</span>
                    )}
                </div>
              </Section>

              {(supersedesTitle || supersededByTitle || doc.supersedesId || doc.supersededById) && (
                <Section title="Supersede chain">
                  <div className="flex flex-col gap-1.5">
                    {doc.supersedesId && (
                      <button
                        type="button"
                        onClick={() => navigate(`/documents/${encodeURIComponent(doc.supersedesId!)}`)}
                        className="inline-flex items-center gap-1.5 text-sm text-zinc-300 hover:text-white"
                      >
                        <ArrowUpRight className="h-3.5 w-3.5" /> Supersedes: {supersedesTitle ?? doc.supersedesId}
                      </button>
                    )}
                    {doc.supersededById && (
                      <button
                        type="button"
                        onClick={() => navigate(`/documents/${encodeURIComponent(doc.supersededById!)}`)}
                        className="inline-flex items-center gap-1.5 text-sm text-amber-300 hover:text-amber-200"
                      >
                        <ArrowUpRight className="h-3.5 w-3.5" /> Superseded by: {supersededByTitle ?? doc.supersededById}
                      </button>
                    )}
                  </div>
                </Section>
              )}
            </CardBody>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardBody className="space-y-3 text-sm">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Owner</p>
                <p className="text-zinc-200">{doc.ownerPoc || '—'}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Authors</p>
                <p className="text-zinc-200">{doc.authors.length ? doc.authors.join(', ') : '—'}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Reviewers</p>
                <p className="text-zinc-200">{doc.reviewers.length ? doc.reviewers.join(', ') : '—'}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Reviewed with</p>
                <p className="text-zinc-200">{doc.reviewedWith.length ? doc.reviewedWith.join(', ') : '—'}</p>
              </div>
              {doc.tags.length > 0 && (
                <div>
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Tags</p>
                  <div className="flex flex-wrap gap-1.5">
                    {doc.tags.map((t) => (
                      <Badge key={t} tone="neutral">#{t}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      <DocumentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        defaultOwner={currentAlias}
        initialDoc={editing}
        goals={goals}
        workstreams={workstreams}
        tasks={tasks}
        services={services}
        decisions={decisions}
        documents={documents}
        serviceTitles={serviceTitles}
        storyTitles={storyTitles}
        decisionTitles={decisionTitles}
        onSubmit={onSubmit}
        structureFromNotes={structureFromNotes}
      />

      <ConfirmDialog
        open={confirmingDelete}
        title="Remove document?"
        confirmLabel="Remove document"
        loading={deleting}
        onConfirm={() => void doDelete()}
        onClose={() => setConfirmingDelete(false)}
        message="Remove this document from the bank? This removes the index entry (not the linked doc) and can't be undone."
      />
    </div>
  );
}

export default DocumentDetailPage;
